import { CircularProgressOverlay } from '@campfire/circular-progress-overlay';
import { formatDistance, unpackToDateTime } from '@campfire/hot-date';
import React, { useMemo, useState } from 'react';
import { Box } from '@material-ui/core';
import { TaskCompleteIcon } from '../../../../../../common/icons/TaskCompleteIcon';
import { TaskPendingReviewIcon } from '../../../../../../common/icons/TaskPendingReviewIcon';
import { useSnackbar } from '../../../../../../global/config/useSnackbar';
import { LeftMenuItemType } from '../../incoming-volunteer-model.gql';
import { IncomingVolunteerCompletedTask } from '../../__generated__/IncomingVolunteerCompletedTask';
import { IncomingVolunteerTask } from '../../__generated__/IncomingVolunteerTask';
import { IncomingVolunteerTaskList } from '../IncomingVolunteerTaskList';
import { StageUnlockButton } from '../StageUnlockButton';
import { IncompleteTasksConfirmDialog } from '../IncompleteTasksConfirmDialog';
import { useProgressApplicationStage } from '../../hooks/useProgressApplicationStage';

interface Props {
  completedTasks?: IncomingVolunteerCompletedTask[];
  locked?: boolean;
  selectedTaskId?: string;
  setLeftMenuItem: (x: LeftMenuItemType) => void;
  applicationId?: string;
  currentStageId?: string | null;
  relevantTasks?: IncomingVolunteerTask[];
  refetch: () => void;
}

interface TaskStage {
  stageId: string;
  stageName: string;
  stageOrder: number;
  automateApproval: boolean;
  tasks: IncomingVolunteerTask[];
}

const IncomingVolunteerTasksSection = (props: Props) => {
  const {
    completedTasks,
    locked,
    selectedTaskId,
    setLeftMenuItem,
    applicationId,
    currentStageId,
    relevantTasks,
    refetch,
  } = props;
  const { setSnackbar } = useSnackbar();
  const { progressToStage, isLoading: isProgressing } = useProgressApplicationStage();

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingStageUnlock, setPendingStageUnlock] = useState<{
    stageId: string;
    stageName: string;
    incompleteTasks: any[];
  } | null>(null);

  const allRequiredTasks = useMemo(() => {
    return (
      relevantTasks
        ?.filter((task) => task.cake.cakeType === 'required' && !task.dateRemoved)
        .sort((a, b) => (a.order < b.order ? -1 : 1)) ?? []
    );
  }, [relevantTasks]);

  // Group tasks by stage
  const taskStages = useMemo(() => {
    const stagesMap = new Map<string, TaskStage>();

    allRequiredTasks.forEach((task) => {
      if (!task.stage) {
        // Handle tasks without stages (legacy behavior)
        const legacyStageId = 'no-stage';
        if (!stagesMap.has(legacyStageId)) {
          stagesMap.set(legacyStageId, {
            stageId: legacyStageId,
            stageName: 'Onboarding Tasks',
            stageOrder: 0,
            automateApproval: false,
            tasks: [],
          });
        }
        stagesMap.get(legacyStageId)!.tasks.push(task);
      } else {
        const stageId = task.stage.taskStageId;
        if (!stagesMap.has(stageId)) {
          stagesMap.set(stageId, {
            stageId,
            stageName: task.stage.name,
            stageOrder: task.stage.order,
            automateApproval: task.stage.automateApproval,
            tasks: [],
          });
        }
        stagesMap.get(stageId)!.tasks.push(task);
      }
    });

    // Sort stages by order
    return Array.from(stagesMap.values()).sort((a, b) => a.stageOrder - b.stageOrder);
  }, [allRequiredTasks]);

  // Count only actual stages (not legacy no-stage)
  const actualStages = taskStages.filter((stage) => stage.stageId !== 'no-stage');
  const totalStages = actualStages.length;

  const handleStageUnlock = async (nextStageId: string, nextStageName: string) => {
    if (!applicationId) return;

    // Find the current stage and check for incomplete tasks
    const currentStage = taskStages.find((stage) => stage.stageId === currentStageId);
    const incompleteTasks =
      currentStage?.tasks.filter((task) => {
        const completedTask = completedTasks?.find((ct) => ct.task.taskId === task.taskId);
        const status = !completedTask
          ? 'none'
          : completedTask.status === 'pending'
          ? 'pending'
          : task.automateApproval || completedTask.status === 'approved'
          ? 'approved'
          : 'none';
        return status !== 'approved';
      }) || [];

    if (incompleteTasks.length > 0) {
      // Show confirmation dialog
      setPendingStageUnlock({
        stageId: nextStageId,
        stageName: nextStageName,
        incompleteTasks: incompleteTasks.map((task) => {
          const completedTask = completedTasks?.find((ct) => ct.task.taskId === task.taskId);
          const status = !completedTask ? 'none' : completedTask.status === 'pending' ? 'pending' : 'approved';
          return {
            taskId: task.taskId,
            title: task.title,
            status,
          };
        }),
      });
      setConfirmDialogOpen(true);
    } else {
      // Proceed with unlock
      await performStageUnlock(nextStageId, nextStageName);
    }
  };

  const performStageUnlock = async (stageId: string, stageName: string) => {
    if (!applicationId) return;

    const success = await progressToStage(applicationId, stageId);

    if (success) {
      setSnackbar({
        open: true,
        message: `${stageName} has been unlocked`,
        variant: 'success',
      });
      refetch();
    } else {
      setSnackbar({
        open: true,
        message: `Failed to unlock ${stageName}`,
        variant: 'error',
      });
    }
  };

  const handleConfirmUnlock = async () => {
    if (pendingStageUnlock) {
      await performStageUnlock(pendingStageUnlock.stageId, pendingStageUnlock.stageName);
    }
    setConfirmDialogOpen(false);
    setPendingStageUnlock(null);
  };

  function taskToTaskListItem(
    task: IncomingVolunteerTask,
    completedTask?: IncomingVolunteerCompletedTask,
    lockedFromVolunteer?: boolean
  ) {
    const status: 'pending' | 'approved' | 'none' = !completedTask
      ? 'none'
      : completedTask?.status === 'pending'
      ? 'pending'
      : task.automateApproval || completedTask?.status === 'approved'
      ? 'approved'
      : 'none';

    // Determine task type based on available properties
    const getTaskType = (): 'referee-check' | 'event-task' | 'manager-completed' | 'user-completed' | undefined => {
      if (task.isRefereeCheckTask) {
        return 'referee-check';
      }
      if ((task as any).isEventTask) {
        return 'event-task';
      }
      if ((task as any).managerCompleted) {
        return 'manager-completed';
      }
      // Don't show chips for regular user-completed tasks
      return undefined;
    };

    return {
      taskId: task.taskId,
      disabled: false,
      selected: task.taskId === selectedTaskId,
      taskTitle: task.title,
      taskSubText: status === 'pending' ? 'Pending review' : '',
      taskRecentActivityStatus: completedTask
        ? `Updated ${formatDistance(unpackToDateTime(completedTask.dateLastUpdated))}`
        : 'Not started',
      onClick: () => {
        return completedTask
          ? setLeftMenuItem({
              taskId: completedTask.task.taskId,
              completedTaskId: completedTask.completedTaskId,
              type: task.__typename,
            })
          : setLeftMenuItem({
              taskId: task.taskId,
              completedTaskId: undefined,
              type: task.__typename,
            });
      },
      icon: status === 'pending' ? <TaskPendingReviewIcon /> : <TaskCompleteIcon isComplete={status === 'approved'} />,
      adminOnly: task.adminOnly,
      roleReq: task.roles,
      taskType: getTaskType(),
      lockedFromVolunteer,
    };
  }

  return (
    <>
      <CircularProgressOverlay isLoading={isProgressing} />
      {taskStages.map((stage, stageIndex) => {
        const isCurrentStage = stage.stageId === currentStageId;
        // For managers on incoming volunteer page, show all stages
        const isVisibleStage = true;

        const stageHeader =
          totalStages > 1 && stage.stageId !== 'no-stage'
            ? `Stage ${stage.stageOrder} of ${totalStages}: ${stage.stageName}`
            : stage.stageName;

        const stageTasks = stage.tasks.map((task) => {
          const matchingCompletedTask = completedTasks?.find(
            (completedTask) => completedTask.task.taskId === task.taskId
          );
          // Determine if task is locked from volunteer view
          // Tasks are locked if they're in future stages (not current and higher order than current)
          const currentStageOrder = taskStages.find((s) => s.stageId === currentStageId)?.stageOrder ?? -1;
          const isTaskLockedFromVolunteer = !currentStageId
            ? stage.stageId !== 'no-stage' // If no current stage, only no-stage tasks are visible
            : stage.stageOrder > currentStageOrder; // Future stages are locked
          return taskToTaskListItem(task, matchingCompletedTask, isTaskLockedFromVolunteer);
        });

        return (
          <Box key={stage.stageId}>
            {isVisibleStage && (
              <>
                <IncomingVolunteerTaskList header={stageHeader} items={stageTasks} locked={locked} />

                {/* Show unlock button for stage progression */}
                {// Current stage with next stage available
                ((isCurrentStage && stageIndex < taskStages.length - 1) ||
                  // No current stage set - show unlock for no-stage to go to first stage
                  (!currentStageId && stage.stageId === 'no-stage' && actualStages.length > 0) ||
                  // No current stage set - show unlock for each real stage to go to next stage
                  (!currentStageId && stage.stageId !== 'no-stage' && stageIndex < taskStages.length - 1)) && (
                  <StageUnlockButton
                    stageName={
                      // From no-stage to first real stage
                      !currentStageId && stage.stageId === 'no-stage' && actualStages.length > 0
                        ? actualStages[0].stageName
                        : // From current stage to next stage
                          taskStages[stageIndex + 1].stageName
                    }
                    onUnlock={() => {
                      const targetStage =
                        !currentStageId && stage.stageId === 'no-stage' && actualStages.length > 0
                          ? actualStages[0]
                          : taskStages[stageIndex + 1];
                      handleStageUnlock(targetStage.stageId, targetStage.stageName);
                    }}
                    disabled={locked || isProgressing}
                    autoUnlockEnabled={
                      // Check if the target stage has automateApproval enabled
                      !currentStageId && stage.stageId === 'no-stage'
                        ? actualStages[0]?.automateApproval || false
                        : taskStages[stageIndex + 1]?.automateApproval || false
                    }
                  />
                )}
              </>
            )}
          </Box>
        );
      })}

      {/* Confirmation dialog for incomplete tasks */}
      {pendingStageUnlock && (
        <IncompleteTasksConfirmDialog
          open={confirmDialogOpen}
          onClose={() => {
            setConfirmDialogOpen(false);
            setPendingStageUnlock(null);
          }}
          onConfirm={handleConfirmUnlock}
          stageName={pendingStageUnlock.stageName}
          incompleteTasks={pendingStageUnlock.incompleteTasks}
        />
      )}
    </>
  );
};

export { IncomingVolunteerTasksSection };
